import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment-timezone";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLocationDot, faBriefcase, faMoneyBill } from "@fortawesome/free-solid-svg-icons";
import Avatar from "../../../components/Avatar";
import BreakDetails from "../../NewScheduler/Component/Drawers/ShiftTrade/SectionDetails/BreakDetails";
import { renderSettingsResourceLabel } from "../../../utils/helpers/settings";
import { useCompanyCurrency, useCompanyTimeFormat } from "../../../utils/hooks/company";
import { renderCurrencySymbol } from "../../../utils/helpers/job";
import { renderUserName } from "../../../utils/helpers/user";
import { useUser } from "../../../utils/hooks/user";
import { calculateSections } from "../../NewScheduler/Component/hooks/useCalculatedSections";

const PENDING_STATUSES = ["pending", "pending-canceled"];

const isUser = (value) => Boolean(value) && typeof value === "object";

const getLogs = (values) => [...(values?.logs || [])]
	.filter(({ user }) => isUser(user))
	.sort((a, b) => moment(a?.date).diff(moment(b?.date)));

const getRequester = (values) => {
	const logs = getLogs(values);
	const opened = logs.find(({ status }) => PENDING_STATUSES.includes(status));
	return opened?.user || logs[0]?.user;
};

const getTaker = (values) => {
	if (isUser(values?.data?.takeUser)) {
		return values.data.takeUser;
	}
	const approvedBy = values?.data?.approvedByUser;
	return approvedBy
		? getLogs(values).find(({ user }) => user?.id === approvedBy)?.user
		: undefined;
};


const getActionUser = (values) => {
	if (PENDING_STATUSES.includes(values?.status)) {
		return undefined;
	}
	const taker = getTaker(values);
	const logs = getLogs(values).filter(({ status, user }) => (
		status === values?.status && user?.id !== taker?.id
	));
	return logs[logs.length - 1]?.user;
};

function OpenShiftRequestView({ values, team }) {
	const { t } = useTranslation();
	const currentUser = useUser();
	const companyTimeFormat = useCompanyTimeFormat();
	const currency = useCompanyCurrency();

	const schedule = useMemo(() => {
		return values?.data?.schedule;
	}, [values?.data?.schedule]);

	const user = useMemo(() => {
		return values?.data?.originalUser || values?.user;
	}, [values?.data?.originalUser, values?.user]);

	const sections = useMemo(() => {
		return calculateSections(schedule?.sections, schedule?.startDate);
	}, [schedule?.sections, schedule?.startDate]);

	const date = useMemo(() => {
		return schedule?.date || values?.data?.startDate;
	}, [schedule?.date, values?.data?.startDate]);

	const start = useMemo(() => {
		return sections?.[0]?.start || values?.data?.startDate;
	}, [sections, values?.data?.startDate]);

	const end = useMemo(() => {
		return sections?.[sections?.length - 1]?.end || values?.data?.endDate;
	}, [sections, values?.data?.endDate]);

	const job = useMemo(() => {
		return sections?.[0]?.job || values?.data?.job;
	}, [sections, values?.data?.job]);

	const location = useMemo(() => {
		return sections?.[0]?.location || values?.data?.location;
	}, [sections, values?.data?.location]);

	const extraPayment = values?.data?.extraPayment;
	const hasExtraPay = extraPayment?.payAmount != null && extraPayment?.payAmount !== "";

	const isApprover = useMemo(() => {
		return (values?.requiredToBeApprovedBy || []).includes(currentUser?.id);
	}, [values?.requiredToBeApprovedBy, currentUser?.id]);

	const title = useMemo(() => {
		const requestTitle = team
			? t("user-want-to-move-this-shift-to-open-shifts", {
				user: renderUserName(user)
			})
			: t("you-want-to-move-this-shift-to-open-shifts");

		const owner = isUser(user) ? renderUserName(user) : "";
		const taker = getTaker(values);
		const actionUser = getActionUser(values);
		const actionUserName = actionUser ? renderUserName(actionUser) : "";

		// Once an employee claims the shift the request is about them taking it, and the
		// managers in `requiredToBeApprovedBy` are the ones that still have to sign off.
		if (taker && owner) {
			const takerName = renderUserName(taker);
			const takenByMe = taker.id === currentUser?.id;

			if (values?.status === "approved") {
				if (takenByMe) {
					return t("you-took-user-shift-from-open-shifts", { user: owner });
				}
				return actionUserName
					? t("user-took-user-shift-from-open-shifts-approved-by", {
						taker: takerName,
						user: owner,
						approver: actionUserName
					})
					: t("user-took-user-shift-from-open-shifts", { taker: takerName, user: owner });
			}

			if (values?.status === "denied") {
				if (takenByMe) {
					return t("your-request-to-take-user-shift-denied", { user: owner });
				}
				return actionUserName
					? t("user-request-to-take-user-shift-denied-by", {
						taker: takerName,
						user: owner,
						approver: actionUserName
					})
					: t("user-request-to-take-user-shift-denied", { taker: takerName, user: owner });
			}

			if (isApprover) {
				return t("user-wants-to-take-user-shift-your-approval", { taker: takerName, user: owner });
			}
			return takenByMe
				? t("you-want-to-take-user-shift-waiting-approval", { user: owner })
				: t("user-wants-to-take-user-shift-waiting-approval", { taker: takerName, user: owner });
		}

		// Nobody claimed the shift yet - the request is still about releasing it.
		const requester = getRequester(values);
		if (!requester) {
			return requestTitle;
		}

		const requesterName = renderUserName(requester);

		if (values?.status === "approved") {
			return t("user-moved-shift-to-open-shifts", { requester: requesterName });
		}

		if (values?.status === "denied") {
			return t("denied-move-this-shift-to-open-shifts");
		}

		if (!PENDING_STATUSES.includes(values?.status) || !user?.id || requester.id === user.id) {
			return requestTitle;
		}

		return team
			? t("user-wants-to-move-user-shift-to-open-shifts", {
				requester: requesterName,
				user: owner
			})
			: t("user-wants-to-move-your-shift-to-open-shifts", { requester: requesterName });
	}, [isApprover, values, team, user, currentUser?.id, t]);

	return (
		<div className="h-100">
			<h3 className="text-primary font-weight-bolder mb-3">
				{title}
			</h3>

			<div className="border rounded p-4 w-100 shadow">
				<div className="d-flex gap-6 w-100">
					<Avatar
						user={user}
						size={60}
						txtStyle={{ fontSize: "10px" }}
					/>

					<div className="w-100">
						<h3 className="text-dark">{moment.parseZone(date).format("ddd, DD MMMM YYYY")}</h3>
						<h4 className="text-muted">
							{moment.parseZone(start).format(companyTimeFormat)
								+ " - " +
								moment.parseZone(end).format(companyTimeFormat)}
						</h4>

						<div className="d-flex flex-column gap-1">
							<div>
								<FontAwesomeIcon icon={faBriefcase} /> {renderSettingsResourceLabel(job)}
							</div>
							<div>
								<FontAwesomeIcon icon={faLocationDot} /> {renderSettingsResourceLabel(location)}
							</div>
							{hasExtraPay && (
								<div>
									<FontAwesomeIcon icon={faMoneyBill} /> {t("extra-pay")}:{" "}
									{`${renderCurrencySymbol(currency)}${extraPayment.payAmount} `}
									<span className="text-muted">
										({t(extraPayment.payType === "hourly" ? "per-hour" : "flat-amount").toLowerCase()})
									</span>
								</div>
							)}
						</div>
					</div>
				</div>

				<div className="d-flex gap-6">
					<div style={{ width: "70px" }}></div>

					<div className="w-100">
						{sections?.length > 0 && (
							<BreakDetails sections={sections} />
						)}

						{values?.data?.reason && (
							<div className="d-flex gap-2 reason-display">
								<span className="text-red font-weight-bolder text-sm">*</span>
								<span className="text-sm">{values?.data?.reason}</span>
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	);
}

export default OpenShiftRequestView;
